import { useContext, useEffect, useState, useRef } from "react";
import Attachments from "./components/Attachments";
import Input from "../../utils/components/Input";
import "react-datepicker/dist/react-datepicker.css";
import API from "../../utils/API";
import { useNavigate, useParams } from "react-router";
import FormHeader from "./components/FormHeader";
import SelectBox from "../../utils/components/SelectBox";
import Forms from "./components/Forms";
import { IoLocationSharp } from "react-icons/io5";
import { useProject } from "../../utils/hooks/useProject";
import { auth } from "../../App/routes";
import { confirmAlert } from "react-confirm-alert";
import AlertModal from "../../utils/components/AlertModal";
import toastr from "toastr";
import { useCan } from "../../utils/hooks/useCan";
import formatSubFormData from "../../utils/helpers/formatSubFormData";
import Checkbox from "../../utils/components/Checkbox";
import { RequestContext } from "../../utils/context/RequestContext";
import getLocation from "../../utils/helpers/getLocation";

const NewForm = ({ settings, translate }) => {
  const [revision, setRevision] = useState([]);
  const [status, setStatus] = useState([]);
  const [metadata, setMetadata] = useState([]);
  const [subForms, setSubForms] = useState([]);
  const [formInfo, setFormInfo] = useState({});

  const [isReadOnly, setIsReadOnly] = useState(false);
  const [isNewTaskAssignmentModalOpen, setIsNewTaskAssignmentModalOpen] =
    useState(false);
  const [isCreateOptionsModalOpen, setIsCreateOptionsModalOpen] =
    useState(false);
  const [isWorkflowCreateModalOpen, setIsWorkflowCreateModalOpen] =
    useState(false);
  const [isWorkflowStartSending, setIsWorkflowStartSending] = useState(false);

  const [isLock, setIsLock] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState([]);
  const [attachments, setAttachments] = useState([]);

  const [taskData, setTaskData] = useState(null);
  const [isTaskDetailsFetched, setIsTaskDetailsFetched] = useState(false);

  const [workflowTemplates, setWorkflowTemplates] = useState([]);

  const [isSendingForm, setIsSendingForm] = useState(false);

  const [resetForm, setResetForm] = useState(false);
  const [formFetched, setFormFetched] = useState(false);

  const { template } = useParams();
  const { project } = useProject();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(window.location.search);
  const can = useCan();

  const { setRequestLoading } = useContext(RequestContext);

  const [shouldAskLocationAccess, setShouldAskLocationAccess] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [userLocationApplied, setUserLocationApplied] = useState(false);

  // Race condition önleme: linked form'dan dönüşte birden fazla API response geldiğinde
  const hasRestoredFromLinkedFormRef = useRef(false);

  useEffect(() => {
    handleInitialize();

    let location = localStorage.getItem("new_form_location_sets");
    if (location) {
      location = JSON.parse(location);
      setSelectedLocation(location);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template, window.location.search]);

  useEffect(() => {
    if (
      project &&
      !isTaskDetailsFetched &&
      (isCreateOptionsModalOpen ||
        isNewTaskAssignmentModalOpen ||
        isWorkflowCreateModalOpen)
    ) {
      if (can.doThat("workflow:read"))
        API.getData(
          `workflow_templates?project_id=${project?.id}&page=1&size=1000`,
        ).then(({ data: res }) => {
          setWorkflowTemplates(res.data);
          setIsTaskDetailsFetched(true);
        });

      if (can.doThat("task:create"))
        API.getData(`task/initialize?project_id=${project?.id}`).then(
          ({ data: res }) => {
            const users = res.data.users.map((user) => ({
              isActionUser: user.is_action_user,
              isInfoUser: user.is_info_user,
              value: user.user_id,
              label: user.user_name,
              secLabel: user.company_name,
            }));

            const companies = res.data.companies.map((company) => ({
              value: company.id,
              label: company.name,
              users: company.users,
            }));

            const groups = res.data.groups.map((group) => ({
              value: group.id,
              label: group.name,
              users: group.users.map((user) => ({
                ...user,
                isActionUser: user.is_action_user,
                isInfoUser: user.is_info_user,
              })),
              secLabel:
                group.users.length > 0 ? group.users[0].company_name : "",
            }));

            setTaskData({ users, companies, groups });

            setIsTaskDetailsFetched(true);
          },
        );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isCreateOptionsModalOpen,
    isNewTaskAssignmentModalOpen,
    isWorkflowCreateModalOpen,
  ]);

  useEffect(() => {
    if (resetForm && formFetched) handleInitialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetForm]);

  useEffect(() => {
    if (shouldAskLocationAccess && !userLocation && !window.chrome) {
      getLocation((location) => setUserLocation(location));
    }
  }, [shouldAskLocationAccess, userLocation]);

  useEffect(() => {
    if (
      userLocation?.lat &&
      userLocation?.lng &&
      subForms.length > 0 &&
      !userLocationApplied
    ) {
      const newSubForms = subForms.map((subForm) => ({
        ...subForm,
        fields: subForm?.fields?.map((field) => {
          if (field?.field_type === "gps" && !field?.value) {
            field.value = { lat: userLocation?.lat, lng: userLocation?.lng };
          }
          return field;
        }),
      }));

      setUserLocationApplied(true);
      setSubForms(newSubForms);
    }
  }, [subForms, userLocation, userLocationApplied]);

  const handleInitialize = () => {
    setFormFetched(false);
    if (subForms && subForms?.length > 0) setSubForms([]);

    if (template)
      API.getData(`/form/initialize/${template}`).then(({ data: res }) => {
        const params = new URLSearchParams(window.location.search);
        const isReturningFromLinkedForm = params.get("nestedForm");
        const isCreatingLinkedForm = params.get("return");
        const parentFormState = JSON.parse(localStorage.getItem("old_form"));

        // Race condition önleme: zaten restore edilmişse tekrar yapma
        if (hasRestoredFromLinkedFormRef.current && isReturningFromLinkedForm) {
          return;
        }

        // Linked form'dan dönüyorsak ve kaydedilmiş state varsa restore et
        if (isReturningFromLinkedForm && parentFormState && !isCreatingLinkedForm && !resetForm) {
          setRevision(parentFormState.revision);
          setStatus(parentFormState.status);
          setMetadata(parentFormState.metadata);
          setSubForms(parentFormState.subForms);
          setFormInfo(parentFormState.formInfo);
          setAttachments(parentFormState.attachments);
          setSelectedStatus(parentFormState.selectedStatus);

          hasRestoredFromLinkedFormRef.current = true;
          localStorage.removeItem("old_form");

          setIsSendingForm(false);
          setRequestLoading(false);
          setResetForm(false);
          setFormFetched(true);
          setIsWorkflowStartSending(false);
          return;
        }

        if (!resetForm) {
          const formInfo = {
            title: res.data.name,
            id: res.data.id,
            name: res.data.name,
          };

          if (res.data.quick_start) {
            formInfo.quick_start = true;
          }

          const newRevision = res.data.revision_set.map((revision) => ({
            id: revision.id,
            value: String(revision.id),
            label: revision.name,
          }));

          setRevision(newRevision);

          setMetadata(
            res.data.headers.map((header) => ({
              ...header,
              data: header.data.map((data) => ({
                value: data.id,
                label: data.name,
                isSelected: false,
              })),
            })),
          );

          const newStatus = res.data.status_set.map((status) => ({
            id: status.id,
            value: status.id,
            label: status.name,
          }));
          setStatus(newStatus);

          if (newStatus.length > 0) setSelectedStatus(newStatus[0]);

          if (newRevision.length > 0) formInfo.revision = newRevision[0];

          setFormInfo(formInfo);
        } else {
          const newFormInfo = {
            ...formInfo,
            title: res.data.name,
            id: res.data.id,
            name: res.data.name,
          };

          setFormInfo(newFormInfo);
        }

        const FIELDS_WITH_ATTACHMENTS = [
          "checkbox",
          "number",
          "rating",
          "selection",
          "rating",
          "date/time",
        ];

        const subForms = res.data.sub_forms.map((subForm, formIndex) => ({
          formIndex,
          index: subForm.index,
          name: subForm.name,
          role: subForm.role,
          isLocked: subForm.locked,
          fields: subForm.controls.map((control, fieldIndex) => {
            const field = {
              fieldIndex,
              field_code: control.field_code,
              isRepeatable: true,
            };

            if (control.field_type === "repeatableGroup") {
              field.fields = [
                control.controls.map((repeatable) => {
                  if (repeatable?.field_type === "gps") {
                    setShouldAskLocationAccess(true);
                  } else if (repeatable?.field_type === "selection") {
                    repeatable.values = repeatable.values.filter(
                      (value, index, self) =>
                        index === self.findIndex((v) => v === value),
                    );
                  }

                  const repeatableField = {
                    ...repeatable,
                    value: repeatable.default_value || "",
                  };

                  if (FIELDS_WITH_ATTACHMENTS.includes(repeatable.field_type)) {
                    repeatableField.field_attach = {
                      image: "",
                      note: "",
                      original_forms: [],
                    };
                  }

                  return repeatableField;
                }),
              ];

              return field;
            }

            if (control?.field_type === "gps") {
              setShouldAskLocationAccess(true);
            } else if (control?.field_type === "selection") {
              control.values = control.values.filter(
                (value, index, self) =>
                  index === self.findIndex((v) => v === value),
              );
            }

            const fields = {
              ...control,
              fieldIndex,
              value: control.default_value || "",
            };

            if (FIELDS_WITH_ATTACHMENTS.includes(control.field_type)) {
              fields.field_attach = {
                image: "",
                note: "",
                original_forms: [],
              };
            }

            return fields;
          }),
        }));

        setSubForms(subForms);

        setIsSendingForm(false);
        setRequestLoading(false);

        setResetForm(false);
        setFormFetched(true);
        setIsWorkflowStartSending(false);
      });
  };

  const handleChangeMetadata = (data, metadataIndex) => {
    const newMetadata = [...metadata];

    newMetadata[metadataIndex].data = newMetadata[metadataIndex].data.map(
      (metadata) => {
        let isSelected;

        if (data.length) {
          isSelected = data.find(({ value }) => value === metadata.value);
        } else {
          isSelected = data.value === metadata.value;
        }

        return {
          ...metadata,
          isSelected,
        };
      },
    );

    setMetadata(newMetadata);
  };

  const handleChangeForm = (e) => {
    const { name, value } = e.target;
    setFormInfo({ ...formInfo, [name]: value });
  };

  const generateUniqueId = () => Date.now() + Math.random();
  const handleAddRemoveRepeatableForm = (
    type,
    index,
    subFormIndex,
    repeatableIndex,
  ) => {
    if (type === "add") {
      const newSubForms = [...subForms];
      const newRepeatableFormFields = newSubForms[subFormIndex]
        .fields[index]
        .fields[0]
        .map((field) => ({
          ...field,
          value: field.default_value || "",
          _uniqueId: generateUniqueId(),
          field_attach: {
            image: "",
            note: "",
            original_forms: [],
          },
        }));

      newSubForms[subFormIndex]
        .fields[index]
        .fields
        .push(
          newRepeatableFormFields,
        );

      setSubForms(newSubForms);
    } else if (type === "remove") {
      const newSubForms = [...subForms];

      newSubForms[subFormIndex]
        .fields[index]
        .fields
        .splice(repeatableIndex, 1);
      setSubForms(newSubForms);
    }
  };

  const handleUpdateFormField = (
    value,
    formIndex,
    fieldIndex,
    repeatableIndex,
    repeatableFieldIndex,
  ) => {
    const newSubForms = [...subForms];

    if (repeatableIndex !== null) {
      newSubForms[formIndex].fields[fieldIndex].fields[repeatableIndex][
        repeatableFieldIndex
        ].value = value;

      setSubForms(newSubForms);
    } else {
      newSubForms[formIndex].fields[fieldIndex].value = value;

      setSubForms(newSubForms);
    }
  };

  const handleUpdateFormFieldAttachment = (
    value,
    formIndex,
    fieldIndex,
    repeatableIndex,
    repeatableFieldIndex,
    attachmentType,
  ) => {
    const newSubForms = [...subForms];

    if (repeatableIndex !== null) {
      newSubForms[formIndex].fields[fieldIndex].fields[repeatableIndex][
        repeatableFieldIndex
        ].field_attach[attachmentType] = value;

      setSubForms(newSubForms);
    } else {
      newSubForms[formIndex].fields[fieldIndex].field_attach[attachmentType] =
        value;

      setSubForms(newSubForms);
    }
  };

  const handleSaveForm = (e, sendData = false, type = null) => {
    if (e) e.preventDefault();

    if (isSendingForm || (sendData && !type)) return;

    if (!selectedStatus?.id || !formInfo?.revision?.id) {
      const message = !selectedStatus?.value
        ? translate("forms.status_empty_alert")
        : translate("forms.revision_empty_alert");

      confirmAlert({
        customUI: ({ onClose }) => (
          <AlertModal
            buttons={[
              {
                label: translate("okay"),
                onClick: () => onClose(),
                classNames: "btn-primary",
              },
            ]}
            message={message}
          />
        ),
      });
      return;
    }

    const headers = metadata.map((metadata) => ({
      metadata_set_id: metadata.id,
      details: metadata.data
        .filter((data) => data.isSelected)
        .map((data) => data.value),
    }));

    const headersCheck = headers.filter((header) => header.details.length);

    if (headersCheck.length !== headers.length) {
      confirmAlert({
        customUI: ({ onClose }) => (
          <AlertModal
            buttons={[
              {
                label: translate("okay"),
                onClick: () => onClose(),
                classNames: "btn-primary",
              },
            ]}
            message={translate("forms.metadata_empty_alert")}
          />
        ),
      });
      return;
    }

    setIsSendingForm(true);
    setRequestLoading(true);

    const formData = {
      name: formInfo.name,
      template_id: template,
      site_set_detail_id:
        selectedLocation?.id ||
        selectedLocation[selectedLocation.length - 1].id,
      status_set_detail_id: selectedStatus.id,
      revision_set_detail_id: formInfo.revision?.id,
      headers: metadata.map((metadata) => ({
        metadata_set_id: metadata.id,
        details: metadata.data
          .filter((data) => data.isSelected)
          .map((data) => data.value),
      })),
      sub_forms: formatSubFormData(subForms, "new_form"),
    };

    API.postData(`forms?project_id=${project.id}`, formData)
      .then(({ data: res }) => {
        // If we're creating a task/workflow, keep loading active
        if (sendData) {
          setRequestLoading(true);
        }

        if (attachments.length) {
          API.putData(
            `form_attachment/${res.data.id}?project_id=${project.id}`,
            {
              files: attachments.map((attachment) => ({
                file_data: attachment.base64,
                file_name: attachment.name,
              })),
            },
          ).then(() =>
            handlePostSubmit(sendData, res.data.id, type, res?.message),
          );
        } else {
          handlePostSubmit(sendData, res.data.id, type, res?.message);
        }

        // Only reset the form if we're NOT creating a task/workflow
        // When creating a task/workflow, we navigate away from this page, so no need to reset
        // When just saving the form, we stay on the same page, so reset for next form entry
        if (!sendData) {
          handleInitialize();
        }
      })
      .catch(() => {
        setIsSendingForm(false);
      });
  };

  const handlePostSubmit = (sendData, id, type, message) => {
    if (!sendData) {
      if (searchParams.get("return")) {
        return handleReturnToForm(id);
      }

      toastr.clear();
      toastr.success(message);

      if (formInfo?.quick_start) {
        setResetForm(true);
        setAttachments([]);
        return;
      }

      setIsSendingForm(false);
      navigate(auth.formDetails.link.replace(":id", id));
    } else {
      if (type === "task") {
        sendData.form_id = id;
        API.postData("tasks", sendData)
          .then(({ data: res }) => {
            if (searchParams.get("return")) {
              return handleReturnToForm(id);
            }

            toastr.clear();
            toastr.success(res.message);

            if (formInfo?.quick_start) {
              setIsNewTaskAssignmentModalOpen(false);
              setResetForm(true);
              return;
            }

            localStorage.removeItem("nested_form");
            localStorage.removeItem("new_form_location_sets");

            navigate(auth.formBoxDetails.link.replace(":id", res.data.id));
          })
          .catch(() => {
            setIsSendingForm(false);
          });
      } else if (type === "workflow-start") {
        setIsWorkflowStartSending(true);
        sendData.form_id = id;
        API.postData("workflow/start", sendData)
          .then(({ data: res }) => {
            if (searchParams.get("return")) {
              setIsSendingForm(false);
              setIsWorkflowStartSending(false);
              return handleReturnToForm(id);
            }

            toastr.clear();
            toastr.success(res.message);

            if (formInfo?.quick_start) {
              setIsWorkflowCreateModalOpen(false);
              setResetForm(true);
              return;
            }

            localStorage.removeItem("nested_form");
            localStorage.removeItem("new_form_location_sets");

            navigate(auth.forms.link);
          })
          .catch((err) => {
            toastr.error(err.response.data.message);

            setIsSendingForm(false);
            setIsWorkflowStartSending(false);
          });
      }
    }
  };

  const handleReturnToForm = (id) => {
    API.getData(`form/${id}?es=true&project_id=${project?.id}`).then(
      ({ data: res }) => {
        const parentFormState = JSON.parse(localStorage.getItem("old_form"));

        if (!parentFormState) {
          localStorage.setItem("nested_form", JSON.stringify(res.data));
          navigate(`${searchParams.get("return")}?nestedForm=${id}`);
          return;
        }

        const updatedParentForm = { ...parentFormState };

        const replacePlaceholderInField = (field) => {
          // Repeatable group: field_attach yok ama iç fields var
          if (!field?.field_attach && field?.fields) {
            return {
              ...field,
              fields: field.fields.map((row) =>
                row.map((repeatableField) => replacePlaceholderInField(repeatableField))
              ),
            };
          }

          if (!field?.field_attach) return field;

          const newField = { ...field };
          if (
            newField.field_attach?.original_forms?.includes("new_form_id_placeholder")
          ) {
            newField.field_attach = {
              ...newField.field_attach,
              original_forms: newField.field_attach.original_forms.map(
                (original_form) =>
                  original_form === "new_form_id_placeholder" ? res.data : original_form,
              ),
            };
          }
          return newField;
        };

        updatedParentForm.subForms = parentFormState.subForms.map((subForm) => ({
          ...subForm,
          fields: subForm.fields.map(replacePlaceholderInField),
        }));

        if (`editedSubForms` in parentFormState) {
          updatedParentForm.editedSubForms = parentFormState.editedSubForms.map((subForm) => ({
            ...subForm,
            fields: subForm.fields.map(replacePlaceholderInField),
          }));
        }

        localStorage.setItem("old_form", JSON.stringify(updatedParentForm));
        localStorage.setItem("nested_form", JSON.stringify(res.data));

        setRevision(updatedParentForm?.revision);
        setStatus(updatedParentForm?.status);
        setMetadata(updatedParentForm?.metadata);
        setSubForms(updatedParentForm?.subForms);
        setFormInfo(updatedParentForm?.formInfo);
        setAttachments(updatedParentForm?.attachments);
        setSelectedStatus(updatedParentForm?.selectedStatus);

        navigate(`${searchParams.get("return")}?nestedForm=${id}`);
      },
    );
  };

  const handleKeepOldForm = () => {
    hasRestoredFromLinkedFormRef.current = false;

    const oldFormState = {
      revision,
      status,
      metadata,
      subForms,
      formInfo,
      attachments,
      selectedStatus,
    };

    localStorage.setItem("old_form", JSON.stringify(oldFormState));
  };

  return (
    <div className="NewForm form-page">
      <div className="container bg-white shadow-sm border border-1 rounded">
        {/** Form actions */}
        <FormHeader
          isReadOnly={isReadOnly}
          selectedStatus={selectedStatus}
          setSelectedStatus={setSelectedStatus}
          setIsReadOnly={setIsReadOnly}
          isCreateOptionsModalOpen={isCreateOptionsModalOpen}
          setIsCreateOptionsModalOpen={setIsCreateOptionsModalOpen}
          isWorkflowCreateModalOpen={isWorkflowCreateModalOpen}
          setIsWorkflowCreateModalOpen={setIsWorkflowCreateModalOpen}
          isNewTaskAssignmentModalOpen={isNewTaskAssignmentModalOpen}
          setIsNewTaskAssignmentModalOpen={setIsNewTaskAssignmentModalOpen}
          isLock={isLock}
          setIsLock={setIsLock}
          statusData={status}
          handleSaveForm={handleSaveForm}
          translate={translate}
          taskData={taskData}
          workflowTemplates={workflowTemplates}
          isSendingFromParent={isWorkflowStartSending}
          template={template}
          isSendingForm={isSendingForm}
          canCreateTask={can.doThat("task:create")}
          canCreateWorkflow={can.doThat("workflow:create")}
        />

        {/** Form actions */}

        {/** Form title */}
        <div className="row mt-5">
          <div className="col-12 d-flex flex-column align-items-start flex-lg-row justify-content-lg-between px-5">
            <p className="fs-4 text-orange">{formInfo?.title}</p>
          </div>
        </div>
        {/** Form title */}

        {/** General Information */}
        <div className="row py-3 mt-4 border-top border-2 ps-5">
          <div className="col-12">
            <p className="fs-5 text-orange">
              {translate("forms.headers.general-informations")}
            </p>
          </div>
          <div className="col-12 col-lg-10">
            <div className="row align-items-center">
              <p className="col-12 col-lg-2 mb-0 fw-bold">
                {translate("forms.headers.form_name")}
              </p>
              <div className="col-12 col-lg-10">
                <Input
                  placeholder={translate("forms.headers.form_name")}
                  name="name"
                  value={formInfo?.name}
                  onChange={handleChangeForm}
                />
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-10 mt-2">
            <div className="row align-items-center">
              <p className="col-12 col-lg-2 mb-0 fw-bold">
                {translate("forms.headers.revision")}
              </p>
              <div className="col-12 col-lg-10">
                <SelectBox
                  options={revision}
                  placeholder={translate("forms.place-holders.select-revision")}
                  value={formInfo?.revision}
                  onChange={(selectedRevision) =>
                    setFormInfo({ ...formInfo, revision: selectedRevision })
                  }
                  disabled={isReadOnly}
                />
              </div>
            </div>
          </div>

          {metadata.map((metadata, index) => (
            <div key={metadata.id} className="col-12 col-lg-10 mt-2">
              <div className="row align-items-center">
                <p className="col-12 col-lg-2 mb-0 fw-bold">{metadata.name}</p>
                <div className="col-12 col-lg-10">
                  <SelectBox
                    options={metadata.data}
                    placeholder={translate(
                      "forms.place-holders.select-metadata",
                      { metadata: metadata.name },
                    )}
                    value={metadata.data.filter((data) => data.isSelected)}
                    onChange={(value) => handleChangeMetadata(value, index)}
                    isMulti={metadata.multi_select}
                  />
                </div>
              </div>
            </div>
          ))}
          {`quick_start` in formInfo && (
            <div className="col-12 col-lg-10 mt-2">
              <Checkbox
                label={translate("forms.quick_start_checkbox")}
                name="quick_start"
                checked={formInfo.quick_start}
                onChange={(checked) =>
                  setFormInfo({ ...formInfo, quick_start: checked })
                }
              />
            </div>
          )}
        </div>

        <div className="row py-3 border-top border-2 ps-3">
          <div className="col-12 py-2">
            <div className="location-info-area d-flex justify-content-start align-items-start">
              <span>
                <IoLocationSharp className="fs-3 text-orange me-2"/>
              </span>
              <div className="locations d-flex flex-column align-items-start">
                <div className="main-location mb-2">
                  {selectedLocation.length > 1
                    ? selectedLocation[selectedLocation.length - 1].name
                    : ""}
                </div>
                <div className="location-breadcrumbs text-gray">
                  {selectedLocation.length
                    ? selectedLocation
                      ?.map((location) => location.name)
                      ?.reduce((prev, curr) => prev + " > " + curr)
                    : ""}
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 py-2">
            <p className="fs-5 text-orange mt-3 mb-0">
              {translate("forms.form_data")}
            </p>
          </div>
        </div>

        <Forms
          forms={subForms}
          handleAddRemoveRepeatableForm={handleAddRemoveRepeatableForm}
          handleUpdateFormField={handleUpdateFormField}
          location={selectedLocation}
          language={settings.language}
          handleUpdateFormFieldAttachment={handleUpdateFormFieldAttachment}
          handleKeepOldForm={handleKeepOldForm}
          userLocation={userLocation}
          translate={translate}
        />

        <Attachments
          title="Attachments"
          data={attachments}
          handleUpload={(files) => setAttachments(files)}
        />
      </div>
    </div>
  );
};
export default NewForm;
